import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate, fmtTime, fmtDateTime, inr, STATUSES } from "@/lib/tps";
import { toast } from "sonner";
import { ChevronLeft, Edit3, MessageSquare, Printer, Trash2, History } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import DeliveryConfirmModal from "@/components/DeliveryConfirmModal";

const NEXT_ACTIONS = {
  New: ["Confirmed", "Cancelled", "Lost"],
  Confirmed: ["In Production", "Cancelled"],
  "In Production": ["Ready", "Cancelled"],
  Ready: ["Out for Delivery", "Delivered"],
  "Out for Delivery": ["Delivered"],
  Delivered: [],
  Cancelled: [],
  Lost: [],
};

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isOwner } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingDelivered, setPendingDelivered] = useState(false); // opens DeliveryConfirmModal

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (s) => {
    // Intercept Delivered transitions when balance > 0
    if (s === "Delivered" && (order?.balance_amount || 0) > 0) {
      setPendingDelivered(true);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${id}/status`, { status: s });
      setOrder(data);
      toast.success(`Marked as ${s}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const finishDelivered = async (collect) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${id}/mark-delivered`, { collect_balance: !!collect });
      setOrder(data);
      toast.success(collect
        ? (isOwner ? "Payment received. Order marked as Delivered." : "Payment collected. Order marked as Delivered.")
        : "Marked as Delivered. Balance still pending.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setPendingDelivered(false);
      setLoading(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Order deleted");
      nav("/orders");
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  if (!order) return <div className="p-6 text-sm text-[#4B5563]">Loading…</div>;
  const item = order.items?.[0] || {};
  const nextOptions = NEXT_ACTIONS[order.status] || [];
  const allOther = STATUSES.filter((s) => s !== order.status);

  return (
    <div className="space-y-3 animate-fade-in" data-testid="order-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={() => nav(-1)} className="h-8" data-testid="detail-back-btn">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={() => nav(`/orders/${id}/edit`)} data-testid="edit-order-btn" disabled={!isOwner} style={{ display: isOwner ? undefined : "none" }}>
            <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
          </Button>
          <Link to={`/templates?order=${id}`}>
            <Button variant="outline" className="h-8 border-[#E8E3DF]" data-testid="whatsapp-btn">
              <MessageSquare className="h-3.5 w-3.5 mr-1" /> WhatsApp
            </Button>
          </Link>
          <Link to={`/print/${id}`} target="_blank">
            <Button variant="outline" className="h-8 border-[#E8E3DF]" data-testid="print-btn">
              <Printer className="h-3.5 w-3.5 mr-1" /> Print
            </Button>
          </Link>
          {isOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="delete-order-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this order?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <Card className="border-[#E8E3DF]">
        <CardContent className="p-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#4B5563]">Order</div>
              <div className="font-heading text-2xl text-[#B86F77]">{order.order_id}</div>
              <div className="text-sm mt-1">{order.customer_name} · {order.customer_phone}{order.customer_instagram ? ` · @${(order.customer_instagram || "").replace(/^@/, "")}` : ""}</div>
            </div>
            <div className="text-right">
              <StatusBadge status={order.status} />
              <div className="text-xs text-[#4B5563] mt-2">Updated {fmtDateTime(order.updated_at)}</div>
            </div>
          </div>

          {/* Status actions */}
          <div className="mt-4 pt-4 border-t border-[#E8E3DF]">
            <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-2">Quick actions</div>
            <div className="flex flex-wrap gap-2">
              {nextOptions.length === 0 && <span className="text-xs text-[#4B5563]">No further actions.</span>}
              {nextOptions.map((s) => (
                <Button
                  key={s} disabled={loading}
                  onClick={() => setStatus(s)}
                  className="h-8 bg-[#B86F77] hover:bg-[#A35D65]"
                  data-testid={`mark-${s.toLowerCase().replace(/\s+/g, "-")}`}
                >Mark as {s}</Button>
              ))}
              <details className="ml-auto">
                <summary className="text-xs text-[#4B5563] cursor-pointer h-8 inline-flex items-center" data-testid="other-status-toggle">Set any status…</summary>
                <div className="mt-2 flex flex-wrap gap-2">
                  {allOther.map((s) => (
                    <Button key={s} variant="outline" className="h-7 border-[#E8E3DF]" onClick={() => setStatus(s)} data-testid={`set-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</Button>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="border-[#E8E3DF] lg:col-span-2">
          <CardContent className="p-4 space-y-3 text-sm">
            <h2 className="font-heading text-base">Cake Details</h2>
            <Row label="Theme" v={item.theme} />
            <Row label="Occasion" v={item.occasion} />
            <Row label="Servings" v={item.servings} />
            <Row label="Weight" v={item.weight ? `${item.weight} kg` : ""} />
            <Row label="Shape" v={item.shape} />
            <Row label="Tier" v={item.tier} />
            <Row label="Quantity" v={item.quantity} />
            <Row label="Flavour" v={item.flavour} />
            <Row label="Cake Message" v={item.cake_message} />
            <Row label="Customization Notes" v={item.customization_notes} />
            <div className="border-t border-[#E8E3DF] pt-3" />
            <h2 className="font-heading text-base">Delivery</h2>
            <Row label="Required Date" v={fmtDate(order.required_date)} />
            <Row label="Required Time" v={fmtTime(order.required_time)} />
            <Row label="Type" v={order.delivery_type} />
            <Row label="Partner" v={order.delivery_method} />
            <Row label="Delivery Address" v={order.delivery_address} />
            {order.google_maps_link && (
              <Row label="Google Maps" v={<a className="text-[#B86F77] hover:underline break-all" href={order.google_maps_link} target="_blank" rel="noreferrer" data-testid="detail-maps-link">{order.google_maps_link}</a>} />
            )}
            {order.delivery_tracking_link && (
              <Row label="Tracking" v={<a className="text-[#B86F77] hover:underline break-all" href={order.delivery_tracking_link} target="_blank" rel="noreferrer">{order.delivery_tracking_link}</a>} />
            )}
            <Row label="Priority" v={order.priority} />
            <div className="border-t border-[#E8E3DF] pt-3" />
            <h2 className="font-heading text-base">Production Notes</h2>
            <div className="text-sm text-[#4B5563] whitespace-pre-wrap">{order.production_notes || "—"}</div>
            <h2 className="font-heading text-base">Ingredients / Prep Notes</h2>
            <div className="text-sm text-[#4B5563] whitespace-pre-wrap">{order.ingredients_notes || "—"}</div>
            <h2 className="font-heading text-base">Staff Notes</h2>
            <div className="text-sm text-[#4B5563] whitespace-pre-wrap">{order.staff_notes || "—"}</div>
            {isOwner && (
              <>
                <h2 className="font-heading text-base">Internal Notes</h2>
                <div className="text-sm text-[#4B5563] whitespace-pre-wrap">{order.notes || "—"}</div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-3">
          {isOwner && (
            <Card className="border-[#E8E3DF]">
              <CardContent className="p-4">
                <h2 className="font-heading text-base mb-2">Pricing</h2>
                <Row label="Total" v={inr(order.total_price)} />
                <Row label="Advance" v={inr(order.advance_amount)} />
                <Row label="Balance" v={<span className="font-medium text-[#B86F77]">{inr(order.balance_amount)}</span>} />
                {order.delivery_charges > 0 && <Row label="Delivery Charges" v={inr(order.delivery_charges)} />}
                <Row label="Method" v={order.payment_method} />
                <Row label="Status" v={order.payment_status} />
                {order.payment_log?.length > 0 && (
                  <div className="pt-3 border-t border-[#E8E3DF] mt-2">
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] mb-1.5">Payment Log</div>
                    <div className="space-y-1.5" data-testid="payment-log">
                      {order.payment_log.map((p, i) => (
                        <div key={i} className="text-xs bg-[#FCF9F9] border border-[#E8E3DF] rounded p-2" data-testid={`payment-log-row-${i}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{inr(p.amount)}</span>
                            <span className="text-[10px] text-[#4B5563]">{fmtDateTime(p.at)}</span>
                          </div>
                          <div className="text-[11px] text-[#4B5563] mt-0.5">
                            {p.status_before} → <span className="text-[#065F46] font-medium">{p.status_after}</span> · by {p.by}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="border-[#E8E3DF]">
            <CardContent className="p-4">
              <h2 className="font-heading text-base mb-2">Images</h2>
              {order.reference_images?.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-[#4B5563] mb-1">Reference Images</div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {order.reference_images.map((b, i) => (
                      <a key={i} href={b} target="_blank" rel="noreferrer">
                        <img src={b} alt="ref" className="w-full h-20 object-cover rounded border border-[#E8E3DF]" data-testid={`detail-ref-img-${i}`} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <ImgRow label="Final Reference Image" src={order.final_reference_image} testId="img-final-ref" />
              <ImgRow label="Final Cake Ready Picture" src={order.final_cake_ready_picture} testId="img-cake-ready" />
              {isOwner && <ImgRow label="Payment Screenshot" src={order.payment_screenshot} testId="img-pay-ss" />}
            </CardContent>
          </Card>
        </div>
      </div>

      {order.status_history && order.status_history.length > 0 && (
        <Card className="border-[#E8E3DF]">
          <CardContent className="p-4">
            <h2 className="font-heading text-base mb-2 flex items-center gap-2"><History className="h-4 w-4" /> Status History</h2>
            <div className="text-sm space-y-1">
              {order.status_history.map((h, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 text-[13px]" data-testid={`history-row-${i}`}>
                  <span className="text-[#4B5563]">{fmtDateTime(h.at)}</span>
                  <span>·</span>
                  <span>{h.from ? <span className="text-[#4B5563]">{h.from} →</span> : <span className="text-[#4B5563]">Created →</span>}</span>
                  <StatusBadge status={h.to} />
                  <span className="text-[#4B5563]">by {h.by}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <DeliveryConfirmModal
        open={pendingDelivered}
        balance={order.balance_amount || 0}
        isOwner={isOwner}
        onCancel={() => setPendingDelivered(false)}
        onCollect={() => finishDelivered(true)}
        onDeliverOnly={() => finishDelivered(false)}
      />
    </div>
  );
}

function Row({ label, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[#4B5563] text-xs">{label}</span>
      <span className="text-right text-sm">{v || "—"}</span>
    </div>
  );
}

function ImgRow({ label, src, testId }) {
  return (
    <div className="mb-3">
      <div className="text-xs text-[#4B5563] mb-1">{label}</div>
      {src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={label} className="w-full max-h-48 object-cover rounded-md border border-[#E8E3DF]" data-testid={testId} />
        </a>
      ) : (
        <div className="text-xs text-[#4B5563] bg-[#FCF9F9] border border-dashed border-[#E8E3DF] rounded-md p-3" data-testid={`${testId}-empty`}>Not uploaded</div>
      )}
    </div>
  );
}
